import moment from 'moment';
import { useMemo } from 'react';
import { DATE_FORMAT } from 'utils';
import useCancellationPolicy from 'hooks/useCancellationPolicy';
import useExchangeId from 'hooks/useExchangeId';
import { useBrandId } from 'AppProvider';
import { ExperienceVersion } from '@kouto/types';
import useListingV3 from 'api/listing/useListingV3/useListingV3';
import useSupportedLanguage from 'utils/data/language/useSupportedLanguage';
import useEventSessions, {
  PublicSessionsQueryParams,
} from './useEventSessions';

interface Props {
  brandId: string;
  eventId?: string | null;
  date?: string;
  latest?: boolean;
}

const useEventBookingSessionSelectedData = ({
  eventId,
  date,
  latest,
}: Props) => {
  const brandId = useBrandId();
  const languageCode = useSupportedLanguage();
  // 1 - GET EVENT FROM API
  const { data: exchangeIds, error: exchangeIdError } = useExchangeId(
    eventId,
    brandId,
  );
  const hasExchangeIdLoaded =
    exchangeIds !== undefined || exchangeIdError !== undefined;
  const [listingId] = exchangeIds ?? [null];

  const {
    data: listing,
    isLoading: isLoadingListing,
    error: listingError,
  } = useListingV3({
    listingId,
    version: latest ? ExperienceVersion.LATEST : ExperienceVersion.PUBLISHED,
    language: languageCode,
  });

  // 2 - GET SESSION FROM API
  const sortedDates = useMemo(() => {
    if (!listing?.eventDates?.length) return [];
    return [...listing.eventDates].sort((a, b) => moment(a).diff(moment(b)));
  }, [listing?.eventDates]);

  const nextScheduledDate = !sortedDates.length
    ? []
    : sortedDates.find((date) =>
        moment(date).startOf('day').isSameOrAfter(moment().startOf('day')),
      );

  const lastScheduledDate = !sortedDates.length
    ? []
    : sortedDates[sortedDates.length - 1];

  const eventSessionsProps: PublicSessionsQueryParams = { latest };
  const daySessionsProps: PublicSessionsQueryParams = { latest };
  // Feed the sessions endpoints the event's collection UUID from the loaded listing (the route value
  // may be a slug); it's the `resource_group_collection` id that backs an event.
  const collectionId = listing?.resourceGroupCollectionId;
  if (collectionId) {
    eventSessionsProps.eventId = collectionId;
    eventSessionsProps.from = moment(nextScheduledDate || moment())
      .subtract(1, 'day')
      .format(DATE_FORMAT);
    eventSessionsProps.to = moment(lastScheduledDate || eventSessionsProps.from)
      .add(1, 'day')
      .format(DATE_FORMAT);

    daySessionsProps.eventId = collectionId;
    if (date) {
      daySessionsProps.from = date;
      daySessionsProps.to = date;
    }
  }
  const { sessions: eventSessions, isLoading: isLoadingEventSessions } =
    useEventSessions(eventSessionsProps);

  const { sessions: daySessions, isLoading: isLoadingDaySessions } =
    useEventSessions(daySessionsProps);

  // 4 - GET CANCELLATION POLICY FROM EVENT OR BRAND SETTINGS
  const { cancellationPolicy, isLoading: isLoadingCancellationPolicy } =
    useCancellationPolicy(listing?.cancellationPolicyId);

  return {
    isLoading:
      isLoadingListing ||
      isLoadingEventSessions ||
      isLoadingDaySessions ||
      isLoadingCancellationPolicy,
    isLoadingListing,
    isLoadingEventSessions,
    isLoadingDaySessions,
    isLoadingCancellationPolicy,
    hasExchangeIdLoaded,
    listingId,
    exchangeIdError,
    listingError,
    listing,
    eventSessions,
    daySessions,
    cancellationPolicy,
  };
};

export default useEventBookingSessionSelectedData;
